// Decode an animated poster (GIF or animated WebP; Voyage's video.mainPoster) into its composited frames, once.
//
//   1. ImageDecoder (WebCodecs) where the browser has it and supports the type: frames come out composited.
//   2. Otherwise GIF: gifuct-js (loaded on demand), patches composited here (disposal 1/2/3).
//   3. Otherwise animated WebP: the RIFF container is split here; each ANMF frame is wrapped as a still WebP,
//      decoded with createImageBitmap and composited (offset, alpha blend or not, dispose to transparent).
//   4. Anything else, or a single frame: one still frame.
// Frames are ImageBitmaps at the image's own size (Voyage posters are 100-220 px wide, 15-50 frames, so a few MB).

export interface AnimFrames {
  width: number;
  height: number;
  frames: ImageBitmap[];
  /** Display time of each frame, ms. */
  delays: number[];
  /** Sum of delays, ms (0 for a still image). */
  total: number;
  /** Which decoder produced the frames (for tests and the HUD). */
  via: 'ImageDecoder' | 'gifuct' | 'webp' | 'still';
}

/** At most this many pixels over all frames (32 MB of RGBA); longer animations are cut. */
const MAX_PIXELS = 8_000_000;

/** Browsers show GIF delays of 10 ms or less as 100 ms; same here. */
const delayMs = (d: number) => (d <= 10 ? 100 : d);

export async function decodeAnimated(url: string): Promise<AnimFrames> {
  const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const buf = new Uint8Array(await res.arrayBuffer());
  const type = sniff(buf) ?? res.headers.get('content-type') ?? '';
  try {
    const viaDecoder = await withImageDecoder(buf, type);
    if (viaDecoder) return viaDecoder;
  } catch (e) {
    console.warn('[poster] ImageDecoder failed, using the fallback decoder', e);
  }
  if (type === 'image/gif') return decodeGif(buf);
  if (type === 'image/webp' && isAnimatedWebp(buf)) return decodeWebp(buf);
  return still(new Blob([buf as Uint8Array<ArrayBuffer>], { type }));
}

function sniff(b: Uint8Array): string | null {
  const s = (o: number, n: number) => String.fromCharCode(...b.subarray(o, o + n));
  if (s(0, 4) === 'GIF8') return 'image/gif';
  if (s(0, 4) === 'RIFF' && s(8, 4) === 'WEBP') return 'image/webp';
  if (b[0] === 0x89 && s(1, 3) === 'PNG') return 'image/png';
  if (b[0] === 0xff && b[1] === 0xd8) return 'image/jpeg';
  return null;
}

function done(width: number, height: number, frames: ImageBitmap[], delays: number[], via: AnimFrames['via']): AnimFrames {
  const total = frames.length > 1 ? delays.reduce((a, d) => a + d, 0) : 0;
  return { width, height, frames, delays, total, via };
}

const maxFrames = (w: number, h: number) => Math.max(1, Math.floor(MAX_PIXELS / Math.max(1, w * h)));

async function still(blob: Blob): Promise<AnimFrames> {
  const bmp = await createImageBitmap(blob);
  return done(bmp.width, bmp.height, [bmp], [0], 'still');
}

// ------------------------------------------------------------------ ImageDecoder (WebCodecs)
interface DecodedImage { image: VideoFrame }
interface ImageDecoderLike {
  completed: Promise<void>;
  tracks: { ready: Promise<void>; selectedTrack: { frameCount: number } | null };
  decode(o: { frameIndex: number }): Promise<DecodedImage>;
  close(): void;
}
interface ImageDecoderCtor {
  new (init: { data: BufferSource; type: string }): ImageDecoderLike;
  isTypeSupported(type: string): Promise<boolean>;
}

async function withImageDecoder(buf: Uint8Array, type: string): Promise<AnimFrames | null> {
  const ID = (globalThis as unknown as { ImageDecoder?: ImageDecoderCtor }).ImageDecoder;
  if (!ID || !type || !(await ID.isTypeSupported(type))) return null;
  const dec = new ID({ data: buf as Uint8Array<ArrayBuffer>, type });
  try {
    await dec.tracks.ready;
    await dec.completed;
    const count = dec.tracks.selectedTrack?.frameCount ?? 1;
    const frames: ImageBitmap[] = [];
    const delays: number[] = [];
    let w = 0, h = 0;
    for (let i = 0; i < count; i++) {
      const { image } = await dec.decode({ frameIndex: i });
      try {
        w = image.displayWidth; h = image.displayHeight;
        if (i >= maxFrames(w, h)) break;
        frames.push(await createImageBitmap(image));
        delays.push(delayMs((image.duration ?? 100_000) / 1000));
      } finally {
        image.close();
      }
    }
    if (!frames.length) return null;
    return done(w, h, frames, delays, 'ImageDecoder');
  } finally {
    dec.close();
  }
}

// ------------------------------------------------------------------ GIF (gifuct-js)
async function decodeGif(buf: Uint8Array): Promise<AnimFrames> {
  const { parseGIF, decompressFrames } = await import('gifuct-js');
  const gif = parseGIF(buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength) as ArrayBuffer);
  const parts = decompressFrames(gif, true);
  const w = gif.lsd.width, h = gif.lsd.height;
  const c = canvas(w, h);
  const g = c.getContext('2d', { willReadFrequently: true })!;
  const patch = canvas(1, 1);
  const pg = patch.getContext('2d')!;
  const frames: ImageBitmap[] = [];
  const delays: number[] = [];
  let prev: (typeof parts)[number] | null = null;
  let saved: ImageData | null = null;
  for (const f of parts.slice(0, maxFrames(w, h))) {
    if (prev?.disposalType === 2) g.clearRect(prev.dims.left, prev.dims.top, prev.dims.width, prev.dims.height);
    else if (prev?.disposalType === 3 && saved) g.putImageData(saved, 0, 0);
    saved = f.disposalType === 3 ? g.getImageData(0, 0, w, h) : null;
    const { width, height, left, top } = f.dims;
    if (width > 0 && height > 0) {
      if (patch.width !== width || patch.height !== height) { patch.width = width; patch.height = height; }
      pg.putImageData(new ImageData(new Uint8ClampedArray(f.patch), width, height), 0, 0);
      g.drawImage(patch, left, top); // drawImage, not putImageData: transparent patch pixels keep what is below
    }
    frames.push(await createImageBitmap(c));
    delays.push(delayMs(f.delay));
    prev = f;
  }
  return done(w, h, frames, delays, 'gifuct');
}

// ------------------------------------------------------------------ animated WebP (own demuxer)
const u24 = (b: Uint8Array, o: number) => b[o]! | (b[o + 1]! << 8) | (b[o + 2]! << 16);
const u32 = (b: Uint8Array, o: number) => (b[o]! | (b[o + 1]! << 8) | (b[o + 2]! << 16) | (b[o + 3]! << 24)) >>> 0;
const tag = (b: Uint8Array, o: number) => String.fromCharCode(b[o]!, b[o + 1]!, b[o + 2]!, b[o + 3]!);

interface Chunk { tag: string; data: Uint8Array }

function chunks(b: Uint8Array, start: number, end: number): Chunk[] {
  const out: Chunk[] = [];
  let o = start;
  while (o + 8 <= end) {
    const n = u32(b, o + 4);
    out.push({ tag: tag(b, o), data: b.subarray(o + 8, Math.min(end, o + 8 + n)) });
    o += 8 + n + (n & 1);
  }
  return out;
}

function isAnimatedWebp(b: Uint8Array): boolean {
  const c = chunks(b, 12, b.length);
  return c[0]?.tag === 'VP8X' && (c[0].data[0]! & 0x02) !== 0 && c.some((x) => x.tag === 'ANMF');
}

/** A still WebP file from one frame's chunks (ALPH + VP8, or VP8L, or VP8). */
function stillWebp(sub: Chunk[], w: number, h: number): Blob {
  const parts: Uint8Array[] = [];
  const put = (t: string, d: Uint8Array) => {
    const hd = new Uint8Array(8);
    for (let i = 0; i < 4; i++) hd[i] = t.charCodeAt(i);
    new DataView(hd.buffer).setUint32(4, d.length, true);
    parts.push(hd, d);
    if (d.length & 1) parts.push(new Uint8Array(1));
  };
  const alpha = sub.some((c) => c.tag === 'ALPH');
  if (alpha) {
    const x = new Uint8Array(10);
    x[0] = 0x10; // alpha flag
    x[4] = (w - 1) & 255; x[5] = ((w - 1) >> 8) & 255; x[6] = ((w - 1) >> 16) & 255;
    x[7] = (h - 1) & 255; x[8] = ((h - 1) >> 8) & 255; x[9] = ((h - 1) >> 16) & 255;
    put('VP8X', x);
  }
  for (const c of sub) if (c.tag === 'ALPH' || c.tag === 'VP8 ' || c.tag === 'VP8L') put(c.tag, c.data);
  const size = parts.reduce((a, p) => a + p.length, 4);
  const riff = new Uint8Array(12);
  riff.set([82, 73, 70, 70], 0); // RIFF
  new DataView(riff.buffer).setUint32(4, size, true);
  riff.set([87, 69, 66, 80], 8); // WEBP
  return new Blob([riff, ...parts] as BlobPart[], { type: 'image/webp' });
}

async function decodeWebp(b: Uint8Array): Promise<AnimFrames> {
  const top = chunks(b, 12, b.length);
  const vp8x = top.find((c) => c.tag === 'VP8X')!.data;
  const w = u24(vp8x, 4) + 1, h = u24(vp8x, 7) + 1;
  const c = canvas(w, h);
  const g = c.getContext('2d')!;
  const frames: ImageBitmap[] = [];
  const delays: number[] = [];
  let dispose: [number, number, number, number] | null = null;
  for (const a of top.filter((x) => x.tag === 'ANMF').slice(0, maxFrames(w, h))) {
    const d = a.data;
    const x = u24(d, 0) * 2, y = u24(d, 3) * 2, fw = u24(d, 6) + 1, fh = u24(d, 9) + 1;
    const dur = u24(d, 12), flags = d[15]!;
    if (dispose) g.clearRect(...dispose);
    const bmp = await createImageBitmap(stillWebp(chunks(d, 16, d.length), fw, fh));
    if (flags & 0x02) g.clearRect(x, y, fw, fh); // do not blend: the frame replaces its rectangle
    g.drawImage(bmp, x, y);
    bmp.close();
    dispose = flags & 0x01 ? [x, y, fw, fh] : null;
    frames.push(await createImageBitmap(c));
    delays.push(delayMs(dur));
  }
  return done(w, h, frames, delays, 'webp');
}

function canvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

/** For tests: decode with a given path (`fallback` skips ImageDecoder). */
export async function decodeAnimatedWith(url: string, fallback: boolean): Promise<AnimFrames> {
  if (!fallback) return decodeAnimated(url);
  const buf = new Uint8Array(await (await fetch(url, { mode: 'cors' })).arrayBuffer());
  const type = sniff(buf);
  if (type === 'image/gif') return decodeGif(buf);
  if (type === 'image/webp' && isAnimatedWebp(buf)) return decodeWebp(buf);
  return still(new Blob([buf], { type: type ?? '' }));
}
